// ownerIntakeModify — the PURE regenerate-vs-intake decision for an out-for-
// signature EPMA Modify. Leaf module (no api/auth imports) so it stays unit-testable
// in isolation; re-exported from ownerIntakeClient for app call sites.
//
// A Modify sends a fresh INTAKE only when the modified config needs NEW owner-
// collected information; otherwise it REGENERATES + re-sends for signature (no
// round-trip). "New info" = a field that is "Ask the owner" (or confirm) in the
// modified config AND has NO existing owner answer on the record. Flipping a field
// FROM Ask TO Yes/No (staff answering it), or a field the owner already answered,
// needs nothing new. Mirror of the server's resolveField / missingRequiredAnswers.

import type { OwnerIntakeAnswers, OwnerIntakeFieldControl } from './ownerIntakeClient';

/** Does this control ask the owner (ask_owner, or a yes/no + confirm)? */
export function ctlIsAsked(c: OwnerIntakeFieldControl): boolean {
  return c.value === 'ask_owner' || c.confirmWithOwner;
}

/** Effective boolean for a control merged with the owner's existing answer. */
export function ctlResolves(c: OwnerIntakeFieldControl, ans: boolean | undefined): boolean {
  if (c.value === 'ask_owner') return ans === true;
  if (c.confirmWithOwner) return ans === undefined ? c.value === 'yes' : ans === true;
  return c.value === 'yes';
}

/** The per-field controls the Modify decision inspects (the owner-facing §21/HOA/
 *  irrigation gates — NOT the §25 notice block, which is always already answered). */
export interface EpmaModifyControls {
  propertyTaxes: OwnerIntakeFieldControl;
  insurancePremiums: OwnerIntakeFieldControl;
  mortgage: OwnerIntakeFieldControl;
  hasIrrigation: OwnerIntakeFieldControl;
  needsWinterizing: OwnerIntakeFieldControl;
  inHoa: OwnerIntakeFieldControl;
  payHoaDues: OwnerIntakeFieldControl;
}

/**
 * The owner-answer keys that would need NEW input for this Modify — asked in the
 * modified config (respecting the irrigation/HOA gates) AND not already answered on
 * the record. EMPTY → regenerate-and-sign; NON-EMPTY → a minimal intake for exactly
 * these field(s). Pure; unit-tested.
 */
export function epmaNewOwnerFields(
  controls: EpmaModifyControls,
  answers: OwnerIntakeAnswers,
): string[] {
  const out: string[] = [];
  const consider = (
    c: OwnerIntakeFieldControl,
    key: keyof OwnerIntakeAnswers,
    gate = true,
  ) => {
    if (gate && ctlIsAsked(c) && answers[key] === undefined) out.push(key as string);
  };
  consider(controls.propertyTaxes, 'whpPaysPropertyTaxes');
  consider(controls.insurancePremiums, 'whpPaysInsurance');
  consider(controls.mortgage, 'whpPaysMortgage');
  consider(controls.hasIrrigation, 'hasIrrigation');
  consider(
    controls.needsWinterizing,
    'needsWinterizing',
    ctlResolves(controls.hasIrrigation, answers.hasIrrigation),
  );
  consider(controls.inHoa, 'inHoa');
  consider(
    controls.payHoaDues,
    'wantsWhpPayHoaDues',
    ctlResolves(controls.inHoa, answers.inHoa),
  );
  return out;
}
